import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { TFunction } from 'i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';

/**
 * 저장된 설정 파일의 구조를 검증한다.
 * LITE/MINI 공통으로 사용하며 deviceType에 따라 타입 검증을 분기.
 */
export function validateConfigFile(
  data: unknown,
  deviceType: DeviceType,
  t: TFunction
): { isValid: boolean; error?: string } {
  if (!data || typeof data !== 'object') {
    return { isValid: false, error: t(i18nKeys.config.error_invalidFileFormat) };
  }

  const d = data as Record<string, unknown>;

  if (!d.deviceInfo || !d.deviceConfig) {
    return { isValid: false, error: t(i18nKeys.config.error_missingRequiredData) };
  }
  if (!d.deviceType) {
    return { isValid: false, error: t(i18nKeys.config.error_missingDeviceType) };
  }
  if (d.version && typeof d.version !== 'string') {
    return { isValid: false, error: t(i18nKeys.config.error_invalidVersion) };
  }
  if (d.timestamp && typeof d.timestamp !== 'string') {
    return { isValid: false, error: t(i18nKeys.config.error_invalidTimestamp) };
  }
  if (typeof d.deviceInfo !== 'object' || d.deviceInfo === null) {
    return { isValid: false, error: t(i18nKeys.config.error_deviceInfoInvalid) };
  }
  if (typeof d.deviceConfig !== 'object' || d.deviceConfig === null) {
    return { isValid: false, error: t(i18nKeys.config.error_configDataInvalid) };
  }

  const dt = d.deviceType;

  if (deviceType === DeviceType.LITE) {
    const isLite =
      dt === DeviceType.LITE ||
      (typeof dt === 'object' && dt !== null && (dt as Record<string, unknown>).isLite === true);
    if (!isLite) {
      return { isValid: false, error: t(i18nKeys.config.error_invalidDeviceType) };
    }
  } else {
    const isMini =
      dt === DeviceType.MINI ||
      (typeof dt === 'object' && dt !== null && (dt as Record<string, unknown>).isMini === true);
    if (!isMini) {
      return { isValid: false, error: t(i18nKeys.config.error_invalidMiniDeviceType) };
    }
  }

  return { isValid: true };
}

/**
 * localStorage 저장용 config data 객체를 생성한다.
 */
export function buildStorageConfigData(
  deviceInfo: unknown,
  deviceConfig: unknown,
  deviceType: DeviceType
) {
  return {
    deviceInfo,
    deviceConfig: deviceConfig as Record<string, unknown>,
    deviceType,
    timestamp: new Date().toISOString(),
    version: '1.0.0',
  };
}

/**
 * 에러 메시지가 BLE/시리얼 연결 끊김을 나타내는지 확인한다.
 */
export function isConnectionLostError(errorMsg: string): boolean {
  return (
    errorMsg.includes('not connected') ||
    errorMsg.includes('not initialized') ||
    errorMsg.includes('BLE')
  );
}

/**
 * 크래들 연결 실패 + 설정 데이터 불일치를 알리는 공용 메시지.
 * (handleConfigSelect에서 반복되던 문자열 조합을 통합)
 */
export function buildCradleMismatchMessage(t: TFunction): string {
  return `${t(i18nKeys.config.msg_cradleConnectFailedDesc)} ${t(
    i18nKeys.config.msg_settingsDataMismatch
  )}`;
}
